import { Controller } from "@hotwired/stimulus"

// Drag-to-resize for the PharOS chat panels — the story-page panel and the Platform app panel
// share it so both open at the same size and resize the same way. Put it on the panel element.
//
// The panel is pinned bottom-right above the FAB, so it grows up and to the left: the handles sit
// on the top-left corner (both axes), the top edge (height) and the left edge (width), and only
// width/height ever change. The chosen size is kept for the tab in sessionStorage under `key`.
const MIN_WIDTH = 380
const MIN_HEIGHT = 480
const SIDE_GAP = 48       // keeps the panel off the viewport's left edge
const FAB_CLEARANCE = 116 // FAB clearance (92) + top gap (24)

export default class extends Controller {
  static values = { key: String }

  connect() {
    this._restore()
  }

  disconnect() {
    this._stopListening()
  }

  startCorner(event) { this._begin(event, { width: true, height: true }) }
  startTop(event)    { this._begin(event, { width: false, height: true }) }
  startLeft(event)   { this._begin(event, { width: true, height: false }) }

  _begin(event, axes) {
    event.preventDefault()
    const rect = this.element.getBoundingClientRect()
    this._drag = { ...axes, x: event.clientX, y: event.clientY, w: rect.width, h: rect.height }
    this.element.classList.add("is-resizing")
    this._onMove = (e) => this._move(e)
    this._onUp = () => this._end()
    window.addEventListener("mousemove", this._onMove)
    window.addEventListener("mouseup", this._onUp)
  }

  // Dragging LEFT (dx < 0) grows the width; dragging UP (dy < 0) grows the height.
  _move(event) {
    const d = this._drag
    if (!d) return
    if (d.width) {
      const w = clamp(d.w - (event.clientX - d.x), MIN_WIDTH, window.innerWidth - SIDE_GAP)
      this.element.style.width = `${w}px`
    }
    if (d.height) {
      const h = clamp(d.h - (event.clientY - d.y), MIN_HEIGHT, window.innerHeight - FAB_CLEARANCE)
      this.element.style.height = `${h}px`
    }
  }

  _end() {
    if (!this._drag) return
    this._stopListening()
    this.element.classList.remove("is-resizing")
    this._drag = null
    try {
      sessionStorage.setItem(this.keyValue, JSON.stringify({ w: this.element.style.width, h: this.element.style.height }))
    } catch (_) { /* sessionStorage can throw in private mode — the size just isn't remembered */ }
  }

  _stopListening() {
    if (this._onMove) window.removeEventListener("mousemove", this._onMove)
    if (this._onUp) window.removeEventListener("mouseup", this._onUp)
    this._onMove = this._onUp = null
  }

  _restore() {
    try {
      const raw = sessionStorage.getItem(this.keyValue)
      if (!raw) return
      const { w, h } = JSON.parse(raw)
      if (w) this.element.style.width = w
      if (h) this.element.style.height = h
    } catch (_) { /* unreadable entry — keep the default size */ }
  }
}

function clamp(value, lo, hi) {
  return Math.min(hi, Math.max(lo, value))
}
